import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useEffect, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html, nothing } from 'lit';
import { SgGroupTimeline } from '../../../history/client/GroupHistoryList.js';
import { m } from '../../../localization/out/messages.js';
import { MartinusTemporaryActions } from '../../../MartinusTemporaryPolicy.js';
import { SGMicrosoftGroupControl } from '../../../microsoft-graph/client/components/sg-microsoft-group-control.js';
import { hasRoles } from '../../../permissions/api/hasRoles.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { Permission, Role } from '../../../permissions/roles.js';
import { Group } from '../../entities/Group.js';
import { GroupDetailsMembers } from './members/GroupDetailsMembers.js';
import { groupAccountingDetailRouter, SgGroupDetailsAccounting } from './SgGroupDetailsAccounting.js';
import { SgGroupDetailsEdit } from './SgGroupDetailsEdit.js';

enum GroupTab {
  INFO = 'Allgemeine Informationen',
  ACCOUNTING = 'Buchhaltung',
  MEMBERS = 'Mitglieder',
  MICROSOFT = 'Microsoft',
  VERLAUF = 'Verlauf',
}

const groupSubPages = {
  members: {
    label: GroupTab.MEMBERS,
    path: '/',
  },
  billingPositions: {
    label: GroupTab.ACCOUNTING,
    path: '/accounting',
  },
  info: {
    label: GroupTab.INFO,
    path: '/info',
  },
  verlauf: {
    label: GroupTab.VERLAUF,
    path: '/verlauf',
    permission: MartinusTemporaryActions.GROUPS_TIMELINE,
  },
  microsoft: {
    label: GroupTab.MICROSOFT,
    path: '/microsoft',
  },
} as const;

export const groupDetailRouter = makeRouter({
  root: {
    path: groupSubPages.info.path,
    render: params => SgGroupDetailsEdit({ groupId: params.groupId }),
    documentTitlePart: groupSubPages.info.label,
  },
  [groupSubPages.billingPositions.label]: {
    path: `${groupSubPages.billingPositions.path}{/*}?`,
    render: params => SgGroupDetailsAccounting({ groupId: params.groupId }),
    subRouter: groupAccountingDetailRouter,
  },
  [groupSubPages.members.label]: {
    path: groupSubPages.members.path,
    render: params => GroupDetailsMembers({ groupId: params.groupId }),
  },
  [groupSubPages.verlauf.label]: {
    path: groupSubPages.verlauf.path,
    render: params => SgGroupTimeline({ groupID: params.groupId }),
  },
  [groupSubPages.microsoft.label]: {
    path: groupSubPages.microsoft.path,
    render: params => SGMicrosoftGroupControl.render({ groupID: params.groupId }),
  },
});

export const SgGroupDetails = stateful((props: { groupId: Maybe<string> }) => {
  const { value: group, loading } = useQuery(
    () => subscribeByID(Group, props.groupId)({ _class: 1, name: 1 }),
    [props.groupId],
  );

  const user = useObservable(() => currentUser(), []);
  const { can: canViewTimeline } = useMartinusABAC({ action: MartinusTemporaryActions.GROUPS_TIMELINE });

  useEffect(() => {
    type GroupDetailRouteName = 'root' | GroupTab.ACCOUNTING | GroupTab.MEMBERS | GroupTab.MICROSOFT | GroupTab.VERLAUF;

    const canViewAccounting = user && hasRoles({ user, roles: [Role.ADMIN, Role.BUCHHALTUNG] });

    addPageHeaderAdditional(
      0,
      PageTabs({
        tabs: Object.values(groupSubPages)
          .map(subPage => ({ key: subPage.label, label: subPage.label }))
          .filter(tab => tab.key !== GroupTab.ACCOUNTING || canViewAccounting)
          .filter(tab => tab.key !== GroupTab.VERLAUF || canViewTimeline),
        activeKey: () =>
          groupDetailRouter.currentRoute.value.route.name === 'root'
            ? GroupTab.INFO
            : groupDetailRouter.currentRoute.value.route.name,
        onSelect: tabKey => {
          if (tabKey === GroupTab.INFO) {
            groupDetailRouter.goTo('root');
          } else {
            groupDetailRouter.goTo(tabKey as GroupDetailRouteName);
          }
        },
      }),
    );
    return () => {
      removePageHeaderAdditional(0);
    };
  }, [user, canViewTimeline]);

  // TODO: loading indicator
  if (!group || loading) return nothing;

  return permissionGuard(
    Permission.EDIT_GROUP,
    () => html`
      ${PageHeader({
        title: `${m[`_class_${group._class}`]()} - ${group?.name}`,
        showBackButton: true,
      })}
      ${groupDetailRouter.outlet.value}
    `,
    {
      context: ANY_CONTEXT,
      forbiddenTemplate: () => html`<div>Forbidden</div>`,
    },
  );
});
